import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from "react";
import type { Task } from "../types";
import { formatLocalDate } from "../lib/workingHours";
import {
  boardDialogStops,
  dialogCanStep,
  presentDialogStops,
  stepDialogStop,
  type DialogStop,
  type DialogStopFocus,
} from "../lib/dialogStops";

interface Snapshot {
  taskIds: string[];
  stops: DialogStop[];
  outcomes: boolean;
}

const EMPTY_SNAPSHOT: Snapshot = { taskIds: [], stops: [], outcomes: false };

export interface DialogCycleFocus {
  /** A saved filter whose only date rule is Today. */
  active: boolean;
  showCompleted: boolean;
}

interface UseDialogCycleArgs {
  taskId: string | undefined;
  tasks: Task[];
  visibleTasks: Task[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  navigate: any;
  editingTask: Task | null;
  activeSubtaskId: string | null;
  navigationFocus?: DialogCycleFocus;
  /** Search side-trip: the arrows do nothing and stay hidden. */
  blockNavigation: boolean;
}

/**
 * The task dialog's previous/next cycle.
 * The order freezes when the dialog opens so an edit that reshuffles the
 * board does not move "next". A Today filter freezes the matching tasks and
 * sub-tasks instead of every card on the board.
 */
export function useDialogCycle({
  taskId,
  tasks,
  visibleTasks,
  navigate,
  editingTask,
  activeSubtaskId,
  navigationFocus,
  blockNavigation,
}: UseDialogCycleArgs) {
  const [snapshot, setSnapshot] = useState<Snapshot>(EMPTY_SNAPSHOT);
  const visibleTasksRef = useRef(visibleTasks);
  visibleTasksRef.current = visibleTasks;
  const showCompletedRef = useRef(navigationFocus?.showCompleted ?? false);
  showCompletedRef.current = navigationFocus?.showCompleted ?? false;
  const outcomesActive = navigationFocus?.active ?? false;

  useEffect(() => {
    if (!taskId) {
      setSnapshot(EMPTY_SNAPSHOT);
      return;
    }
    setSnapshot((prev) => {
      if (prev.taskIds.includes(taskId) && prev.outcomes === outcomesActive) return prev;
      const visible = visibleTasksRef.current;
      const focus: DialogStopFocus = {
        active: outcomesActive,
        showCompleted: showCompletedRef.current,
        today: formatLocalDate(new Date()),
      };
      return {
        taskIds: visible.map((task) => task.id),
        stops: boardDialogStops(visible, focus),
        outcomes: outcomesActive,
      };
    });
  }, [taskId, outcomesActive]);

  const setNavigationSnapshotIds = useCallback((action: SetStateAction<string[]>) => {
    setSnapshot((prev) => {
      const taskIds = typeof action === "function" ? action(prev.taskIds) : action;
      if (taskIds.length === 0) return EMPTY_SNAPSHOT;
      return { ...prev, taskIds };
    });
  }, []);

  const navigationTasks = useMemo(() => {
    if (snapshot.taskIds.length === 0) return visibleTasks;
    const taskById = new Map(tasks.map((task) => [task.id, task]));
    return snapshot.taskIds
      .map((id) => taskById.get(id))
      .filter((task): task is Task => !!task);
  }, [snapshot.taskIds, tasks, visibleTasks]);

  const focus: DialogStopFocus = {
    active: outcomesActive,
    showCompleted: navigationFocus?.showCompleted ?? false,
    today: formatLocalDate(new Date()),
  };
  const liveStops = boardDialogStops(visibleTasks, focus);
  const usingSnapshot = snapshot.taskIds.length > 0;
  const stops = presentDialogStops(usingSnapshot ? snapshot.stops : liveStops, tasks);
  const outcomes = usingSnapshot ? snapshot.outcomes : outcomesActive;
  const cardOrder = (usingSnapshot ? navigationTasks : visibleTasks).map((task) => task.id);
  const current = {
    taskId: editingTask?.id ?? taskId ?? "",
    subtaskId: activeSubtaskId,
  };

  const canNavigate =
    !blockNavigation &&
    !!current.taskId &&
    dialogCanStep(stops, cardOrder, current, outcomes, tasks);

  const handleNavigateTask = (direction: "next" | "prev") => {
    if (!editingTask || blockNavigation) return;
    const next = stepDialogStop(
      stops,
      cardOrder,
      { taskId: editingTask.id, subtaskId: outcomes ? activeSubtaskId : null },
      direction,
      outcomes,
      tasks,
    );
    if (!next) return;
    navigate({
      to: "/task/$taskId",
      params: { taskId: next.taskId },
      search: (prev: Record<string, unknown>) => {
        if (!outcomes) return prev;
        if (next.subtaskId) return { ...prev, subtask: next.subtaskId };
        const { subtask: _subtask, ...rest } = prev;
        return rest;
      },
      replace: true,
    });
  };

  return {
    navigationSnapshotIds: snapshot.taskIds,
    setNavigationSnapshotIds,
    navigationTasks,
    canNavigate,
    handleNavigateTask,
  };
}
